import type { MessageContext } from '@/domain/answerReferral';
import type { Attachment } from '@/domain/attachment';
import type { CheckoutPaymentStatus } from '@/domain/checkout';
import {
  DATA_DISPLAY_TYPES,
  MESSAGE_SUB_TYPES,
  MESSAGE_TYPES,
  type MessageSubType,
  type MessageType,
  type PromptsSource,
} from '@/domain/messageEnums';

export interface CompositeMessageContent {
  messages: Array<CompositeMessagePart>;
}

export type CompositeMessagePart =
  | CompositeMessageTextPart
  | CompositeMessageHtmlPart
  | CompositeMessageDataDisplayPart;

export interface CompositeMessageTextPart {
  type: typeof MESSAGE_SUB_TYPES.TEXT;
  content: { body: string };
}

export interface CompositeMessageHtmlPart {
  type: typeof MESSAGE_SUB_TYPES.HTML;
  content: { body: string };
}

export interface Product {
  pageUrl: string;
  name: string;
  description?: string;
  primaryImageUrl: string;
  secondaryImageUrl?: string;
  extraAttributes?: unknown;
}

export interface ProductListDisplayContent {
  products: Product[];
}

export interface CompositeMessageDataDisplayPart {
  type: typeof MESSAGE_SUB_TYPES.DATA_DISPLAY;
  content: DataDisplayContent;
}

export interface CreditCardRequestContent {
  currency: string;
  itemId: string;
  memo: string;
  monetaryUnit: number;
  requestStatus: string;
}

/**
 * Shared by a standalone DATA_DISPLAY message and a DATA_DISPLAY part of a
 * composite: the wire payload is identical, so both are narrowed by the same
 * validator and render through the same component.
 */
export type DataDisplayContent =
  | { displayContent: ProductListDisplayContent; type: typeof DATA_DISPLAY_TYPES.PRODUCT_LIST }
  | { displayContent: Record<string, unknown>; type: typeof DATA_DISPLAY_TYPES.ORDER_STATUS };

export interface FlexibleCardContent {
  template: string;
  data: unknown;
}

// export type FlexibleCardData = {
//   [key: string]: FlexibleCardDataValue;
// };

// export type FlexibleCardDataValue =
//   | boolean
//   | number
//   | string
//   | Array<FlexibleCardDataValue>
//   | Record<string, unknown>;

/**
 * One selectable button on a quick reply request.
 *
 * The wire also carries an optional `automationId` per option, which tells the
 * backend to start a Visual IVR ("self-service thread") instead of forwarding
 * the label as text. That product is deprecated and has no place in V2, so the
 * field is deliberately absent here: the response echoes the option set rebuilt
 * from this type, which makes it structurally impossible for V2 to trigger one.
 * An automation-backed option therefore degrades to a plain text send of its
 * label — the same outcome V1 produces when it strips the field.
 */
export interface QuickReplyOption {
  id: string;
  label: string;
}

export interface QuickReplyRequestContent {
  body: string;
  options?: QuickReplyOption[];
}

/**
 * The structured `subTypeContent` a quick reply response carries on the wire.
 * `quickReplyRequest` echoes the request the selection answers — the backend
 * resolves `selectedOptionId` against that echoed option set.
 */
export interface QuickReplyResponsePayload {
  quickReplyRequest: QuickReplyRequestContent;
  requestMessageId: string;
  selectedOptionId: string;
}

// `body` is the selected option's label verbatim, mirroring
// ButtonPromptSelectedContent: it is what renders as the consumer's bubble, so a
// consumer that knows nothing of quick replies still shows the right text.
export interface QuickReplyResponseContent extends QuickReplyResponsePayload {
  body: string;
}

export interface OfferedPrompt {
  id: string;
  text: string;
}

export interface ButtonPromptSelectedPayload {
  offeredPrompts: OfferedPrompt[];
  promptsSource: PromptsSource;
  selectedPromptId: string;
  launcherId: string;
  pageUrl: string;
}

// `body` is the selected prompt's text verbatim (the degradation safety net so
// stale/unknown consumers render a correct plain bubble). Named `body` to match
// every other renderable content type; the inbound converter maps the wire's
// `text` field onto it.
export interface ButtonPromptSelectedContent extends ButtonPromptSelectedPayload {
  body: string;
}

/**
 * An express-checkout completion. Carries no payment token, nonce or payer
 * detail, and none may be added: this is persisted as a conversation item that
 * agents, transcripts and the warehouse all see.
 */
export interface CheckoutCompletedPayload {
  providerId: string;
  methodLabel: string;
  amount: string;
  currency: string;
  status: CheckoutPaymentStatus;
  orderRef?: string;
  /** Consumer-facing order/receipt page on the customer's own site, so the
   * agent can hand the consumer a way back to their order. An http(s) URL;
   * it may carry the site's own session params, so it stays out of logs and
   * analytics — the conversation is its only destination. */
  receiptUrl?: string;
}

export interface CheckoutCompletedContent extends CheckoutCompletedPayload {
  body: string;
}

export interface SessionEndContent {
  body: string;
}

export interface TextContent {
  body: string;
}

export interface ImageContent {
  fileName: string;
  mediaUrl: string;
}

export interface FileContent {
  fileName: string;
  mediaUrl: string;
}

// Discriminated union of MessageContent based on subType
export type MessageContent =
  | ButtonPromptSelectedContent
  | CheckoutCompletedContent
  | CompositeMessageContent
  | CreditCardRequestContent
  | DataDisplayContent
  | FlexibleCardContent
  | QuickReplyRequestContent
  | QuickReplyResponseContent
  | SessionEndContent
  | TextContent
  | ImageContent
  | FileContent;

// Base message properties shared by all message types
interface BaseMessage {
  id: string;
  type: MessageType;
  isUser: boolean;
  date: string; // ISO string
  isFromFetch?: boolean;
  gladlyCustomerId?: string;

  /**
   * Provider-supplied correlation id, relayed unchanged from the automation
   * provider on the message envelope (`externalMessageId`). Present on a
   * streamed agent reply's final message so the widget can finalize the
   * matching streaming bubble (see {@link StreamingMessage}). Absent on most
   * messages (customer sends, say-steps, lifecycle) — an absent id matches no
   * live bubble and the message renders plainly, so a non-stream message can
   * never truncate a streamed reply.
   */
  externalMessageId?: string;

  /**
   * Transport ids (`ulid`s) of the received messages merged into this bubble. A
   * reply longer than the per-channel limit (CHAT splits >2048 chars) arrives as
   * several transport messages sharing one `externalMessageId`; they concatenate
   * into the first (anchor) message rather than rendering as N bubbles. Tracks
   * contributors so a redelivered chunk is not appended twice. Set only when a
   * second chunk merges in.
   */
  _streamChunkIds?: string[];

  _hasError?: boolean;
  _errors?: string[];
  /** Set by the server's echo; a transport rejection that lands afterwards is moot. */
  _confirmed?: boolean;

  [key: string]: unknown; // Allow other properties
}

// Discriminated union of Message types based on subType
export interface ButtonPromptSelectedMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.BUTTON_PROMPT_SELECTED;
  content: ButtonPromptSelectedContent;
}

export interface CheckoutCompletedMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.CHECKOUT_COMPLETED;
  content: CheckoutCompletedContent;
}

export interface CompositeMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.COMPOSITE_MESSAGE;
  content: CompositeMessageContent;
}

export interface CreditCardRequestMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.CREDIT_CARD_REQUEST;
  content: CreditCardRequestContent;
}

export interface DataDisplayMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.DATA_DISPLAY;
  content: DataDisplayContent;
}

export interface FlexibleCardMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.FLEXIBLE_CARD;
  content: FlexibleCardContent;
}

export interface QuickReplyRequestMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.QUICK_REPLY_REQUEST;
  content: QuickReplyRequestContent;
}

export interface QuickReplyResponseMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.QUICK_REPLY_RESPONSE;
  content: QuickReplyResponseContent;
}

export interface SessionEndMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.SESSION_END;
  content: SessionEndContent;
}

export interface TextMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.TEXT;
  content: TextContent;
}

export interface HtmlMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.HTML;
  content: TextContent;
}

export interface ImageMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.IMAGE;
  content: ImageContent;
}

export interface FileMessage extends BaseMessage {
  subType: typeof MESSAGE_SUB_TYPES.FILE;
  content: FileContent;
}

// Discriminated union of all Message types
export type Message =
  | ButtonPromptSelectedMessage
  | CheckoutCompletedMessage
  | CompositeMessage
  | CreditCardRequestMessage
  | DataDisplayMessage
  | FlexibleCardMessage
  | QuickReplyRequestMessage
  | QuickReplyResponseMessage
  | SessionEndMessage
  | TextMessage
  | HtmlMessage
  | ImageMessage
  | FileMessage;

export interface PendingMessage {
  id: string;
  messageContext?: MessageContext;
  subType: MessageSubType;
  text: string;
  /** Sent verbatim once auth completes; absent on a plain TEXT message. */
  subTypeContent?: ButtonPromptSelectedPayload | CheckoutCompletedPayload | QuickReplyResponsePayload;

  [key: string]: unknown;
}

// Re-export for convenience
export { MESSAGE_SUB_TYPES, MESSAGE_TYPES, type MessageSubType, type MessageType };

export interface StreamingMessageDelta {
  /** Turn-monotonic ordering key carried end-to-end on each TEXT_DELTA. */
  sequence: number;
  /** Additive fragment of the reply (not a cumulative snapshot of the reply so far). */
  text: string;
}

/**
 * A live, in-progress streamed agent reply assembled from TEXT_DELTA events,
 * keyed by its `externalMessageId`. Turn-scoped: it exists only while a reply
 * streams and is cleared on id-scoped finalize, supersede by a newer stream,
 * or `clearMessages`. Never persisted (the messages slice is excluded from
 * `partialize`).
 */
export interface StreamingMessage {
  externalMessageId: string;
  deltas: StreamingMessageDelta[];
}

export interface MessagesState {
  /** Messages sent and received, keyed by message id */
  data: Record<string, Message>;
  /** Pending messages to be sent */
  pending: PendingMessage[];
  /** Attachments queued while authentication is in progress */
  pendingAttachments: Attachment[];
  /** Initial message text submitted from bootstrap, rendered optimistically before auth completes */
  pendingInitialMessage: string | null;
  /** Welcome message shown when chat starts */
  welcomeMessage: Message | null;
  /**
   * In-progress streamed replies, keyed by `externalMessageId`. At most one is
   * live at a time (a new stream supersedes the prior one); kept as a record so
   * the model generalizes if concurrent streams ever arrive. Turn-scoped.
   */
  streamingMessages: Record<string, StreamingMessage>;
  /**
   * External message ids whose stream has finalized or been superseded. A
   * closed id can never reopen a bubble, so a reordered or duplicated straggler
   * delta from a finished stream is dropped. Bounded (oldest entries trimmed).
   */
  closedIds: string[];
}

/**
 * Initial messages state
 */
export function getInitialMessagesState(): MessagesState {
  return {
    data: {},
    pending: [],
    pendingAttachments: [],
    pendingInitialMessage: null,
    welcomeMessage: null,
    streamingMessages: {},
    closedIds: [],
  };
}

export function isUnreadAgentMessage(message: Message, cutoff: string): boolean {
  // ISO 8601 strings from toISOString() are lexicographically sortable.
  return !message.isUser && message.date > cutoff;
}
